import React from 'react'
import styled from 'styled-components'
import {
  Box,
  Button,
  ElevationLevel,
  Flex,
  Responsive,
  Fixed,
  Relative,
} from '@revolut/ui-kit'

import { Loader } from 'components/Loader'
import { useKeyDown } from 'hooks/useKeyPress'
import { Keys } from 'constants/common'

import { BackButton } from '../BackButton'
import { CloseButton } from '../CloseButton'

const Wrapper = styled(Flex).attrs({
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: '100vh',
  bg: { all: 'white', md: 'grey-95' },
})`
  position: relative;
`

export type Props = {
  title?: string
  subtitle?: string
  buttonText?: string
  onSubmit?: () => void
  onBack?: () => void
  onClose?: () => void
  isSubmitting?: boolean
  isLoading?: boolean
  header: React.ReactNode
  bottom?: React.ReactNode
  footer?: React.ReactNode
}

export const CardSlide: React.FC<Props> = ({
  onSubmit,
  buttonText = 'Continue',
  isSubmitting = false,
  isLoading = false,
  onBack,
  onClose,
  header,
  bottom,
  footer,
  children,
  title,
  subtitle,
  ...rest
}) => {
  useKeyDown(
    Keys.ENTER,
    () => {
      onSubmit && onSubmit()
    },
    [onSubmit],
  )

  return (
    <Responsive breakpoint='md-*'>
      {({ isMatched }: { isMatched: boolean }) => (
        <Wrapper {...rest}>
          {isLoading ? (
            <Flex alignItems='center' minHeight='100vh'>
              <Loader />
            </Flex>
          ) : (
            <>
              <BackButton onBack={onBack} />
              <CloseButton onClose={onClose} />
              <Flex>
                <Relative
                  left={{ all: '50%', md: '33.33%' }}
                  style={{ transform: 'translate(-50%)' }}
                  maxWidth='28rem'
                  width='100vw'
                  mt={{ all: 0, md: '4rem' }}
                  mb={{ all: '4rem', md: '8rem' }}
                  pt={{ all: 0, md: '2.5rem' }}
                  pb={{ all: 0, md: '1.5rem' }}
                  radius={{ all: undefined, md: 'sheet' }}
                  elevation={isMatched ? 300 : 0}
                  bg='white'
                >
                  <Flex flexDirection='column' alignItems='stretch'>
                    <Box pt='2.25rem' pb='2.5rem' px={{ all: '1.5rem', md: '2.5rem' }}>
                      {header}
                    </Box>
                    <Box height='1px' bg='grey-100' width='100%' />
                    <Flex
                      flex='1'
                      flexDirection='column'
                      justifyContent='center'
                      minWidth={{ all: '100%', md: '28rem' }}
                      p='1.5rem'
                      px={{ all: '1rem', md: '2rem' }}
                    >
                      {children}
                    </Flex>
                    {bottom && (
                      <>
                        <Box height='1px' bg='grey-100' width='100%' />
                        <Box p='2rem' px={{ all: '1rem', md: '2rem' }}>
                          {bottom}
                        </Box>
                      </>
                    )}
                  </Flex>
                </Relative>
              </Flex>
              {footer ? (
                <Box
                  maxWidth='28rem'
                  mx='auto'
                  px={{ all: '1rem', md: 0 }}
                  flex='1'
                  mt='3rem'
                  mb='6rem'
                >
                  {footer}
                </Box>
              ) : null}
            </>
          )}
          {onSubmit && (
            <Fixed
              left={{ all: '50%', md: '33.33%' }}
              style={{ transform: 'translate(-50%)' }}
              py='.25rem'
              px={{ all: '.5rem', md: 0 }}
              width='100%'
              maxWidth='22rem'
              bottom='2rem'
            >
              <Button
                elevation={ElevationLevel.MEDIUM}
                disabled={isSubmitting}
                onClick={onSubmit}
              >
                {buttonText}
              </Button>
            </Fixed>
          )}
        </Wrapper>
      )}
    </Responsive>
  )
}
